import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { LoadingState } from '../components/ui';
import { authService, getErrorMessage } from '../services/api';
import { normalizeInviteCode, setPendingInviteCode } from '../utils/invite';

/**
 * ACC-16: the link in the confirmation email (/confirm-email?token=…[&invite=CODE]). Confirms the address, then the
 * login page with the address filled in; the password is asked there, so a link that reached the wrong mailbox opens
 * nothing. A class invite in the link (CLS-9) is followed at that login, also when the email is opened on another
 * device than the one that registered.
 */
const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // Once: React's development mode runs effects twice, and a second confirmation would only say "already confirmed".
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = searchParams.get('token');
    const inviteCode = normalizeInviteCode(searchParams.get('invite'));
    if (inviteCode) {
      setPendingInviteCode(inviteCode);
    }
    if (!token) {
      setError('This confirmation link is incomplete. Open the link from the email again, or ask for a new one below.');
      return;
    }
    authService.confirmEmail(token)
      .then(({ status, email }) => {
        // `replace`: the token leaves the address bar and the history.
        navigate(inviteCode ? `/login?invite=${inviteCode}` : '/login', {
          replace: true,
          state: {
            email,
            message: status === 'confirmed'
              ? 'Your email is confirmed. Log in to start.'
              : 'Your email was already confirmed. Log in to continue.',
          },
        });
      })
      .catch((err) => setError(getErrorMessage(err, 'This confirmation link has expired or is no longer valid.')));
  }, [navigate, searchParams]);

  if (!error) {
    return (
      <AuthShell brandAsHeading={false}>
        <h1 className="sr-only">Confirming your email</h1>
        <LoadingState label="Confirming your email..." className="py-6" />
      </AuthShell>
    );
  }

  return (
    <AuthShell brandAsHeading={false}>
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">This link doesn't work</h1>
      <div className="alert alert-error mt-4" role="alert">
        <AlertCircle />
        <span>{error}</span>
      </div>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Link to="/check-email" replace className="btn btn-primary btn-lg flex-1">Send a new link</Link>
        <Link to="/login" replace className="btn btn-secondary btn-lg flex-1">Log in</Link>
      </div>
    </AuthShell>
  );
};

export default ConfirmEmail;
